<template>
	<v-card class="scene-editor fill-wh" :loading="loading">
		<v-toolbar class="toolbar">
			<v-btn v-if="!!selectedScene" icon @click="selectedScene = null">
				<v-icon>fas fa-times</v-icon>
			</v-btn>

			<v-toolbar-title>
				{{ selectedScene ? selectedScene.name : "No scene selected" }}
			</v-toolbar-title>

			<v-spacer />

			<v-btn v-if="!!selectedScene" icon @click="handleEditScene">
				<v-icon>fas fa-pencil-alt</v-icon>
			</v-btn>
		</v-toolbar>

		<div class="master-detail-container">
			<layout-master-detail :show-details="showDetails" :aside-size="60">
				<SceneList :items="scenes" :selected.sync="selectedScene" />

				<template v-if="selectedScene" #detail>
					<!-- check user role for advanced support -->
					<v-expansion-panels
						v-model="selectedSceneEditor"
						mandatory
						accordion
						flat
						tile
					>
						<!-- wizard accordion -->
						<v-expansion-panel>
							<v-expansion-panel-header>
								<h3>Wizard</h3>
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<WizardSceneWrapper :scene="selectedScene" />
							</v-expansion-panel-content>
						</v-expansion-panel>
						<!-- advanced scene accordion -->
						<v-expansion-panel v-if="canEditScenes">
							<v-expansion-panel-header>
								<h3>Advanced Scene Editor</h3>
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<TriggerList :scene="selectedScene" />
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-expansion-panels>
				</template>
			</layout-master-detail>
		</div>

		<!-- Speed Dial -->
		<ScenesSpeedDial
			:show-add-scene="canEditScenes"
			@action:add-scene="showAddSceneDialog()"
			@action:wizard="showWizardDialog"
		/>

		<!-- Dialogs -->
		<RenameSceneDialog
			ref="renameSceneDialog"
			:placeholder="selectedScene && selectedScene.name"
		/>
		<AddSceneDialog ref="addSceneDialog" />
		<WizardSceneDialog ref="WizardDialog" />
		<dialog-confirm-dialog
			ref="confirmRemoveDialog"
			:body="strings.removeSceneConfirmation"
		/>
	</v-card>
</template>

<style lang="scss" scoped>
.master-detail-container {
	position: relative;
	min-height: calc(100% - 56px); // 56px toolbar

	@media #{map-get($display-breakpoints, 'md-and-up')} {
		min-height: calc(100% - 64px); // 64px toolbar
	}
}
</style>

<script>
import { mapActions, mapGetters } from "vuex";

import strings from "@/utils/strings";
import ability from "@/services/ability"

import TriggerList from "./scenes/TriggerList";
import SceneList from "./scenes/SceneList";
import ScenesSpeedDial from "./scenes/ScenesSpeedDial";

import RenameSceneDialog from "./scenes/dialogs/RenameSceneDialog";
import AddSceneDialog from "./scenes/dialogs/AddSceneDialog";

export default {
	components: {
		TriggerList,
		SceneList,
		ScenesSpeedDial,
		RenameSceneDialog,
		AddSceneDialog,
	},

	data: () => ({
		loading: false,
		strings: strings.live.scenes,
		selectedSceneEditor: 0,
	}),

	computed: {
		...mapGetters({
			system: "session/system",
			selectedActions: "scenes/selectedActions",
			availableWizards: "scenes/availableWizards",
			editWizard: "scenes/editMode",
		}),

		scenes() {
			return this.$store.state.scenes.scenes;
		},

		showDetails() {
			return !!this.selectedScene;
		},

		selectedScene: {
			get() {
				return this.$store.getters["scenes/selectedScene"];
			},
			set(scene) {
				const sceneId = scene ? scene.guid : null;
				this.$store.commit("scenes/setSelectedSceneId", sceneId);
				this.toggleEditWizard(false);
			},
		},

		canEditScenes() {
			return ability.can("edit", "Scenes");
		},

		isWizardSelected() {
			return this.selectedSceneEditor === 0;
		},
	},

	watch: {
		isWizardSelected() {
			this.toggleEditWizard(false);
		},
	},

	beforeRouteEnter(to, from, next) {
		return next((vm) => {
			vm.loadRemoteController(vm.system.id);
		});
	},

	beforeRouteLeave(to, from, next) {
		// Make sure we disconnect from controller before leaving
		this.$store.commit("controller/resetState");
		return next();
	},

	async beforeMount() {
		this.loading = true;
		try {
			// Load descriptions
			await this.$store.dispatch("descriptions/load");
		} finally {
			this.loading = false;
		}
	},

	methods: {
		...mapActions({
			loadRemoteController: "controller/loadRemote",
			addScene: "scenes/addScene",
			addWizard: "scenes/addWizard",
			renameScene: "scenes/renameScene",
			removeScene: "scenes/removeScene",
		}),

		showAddSceneDialog() {
			this.$refs.addSceneDialog.pop(this.addScene);
		},

		showWizardDialog(wizard) {
			this.$refs.WizardDialog.pop(wizard, (form) =>
				this.addWizard({ wizard, form })
			);
		},

		handleEditScene() {
			this.isWizardSelected
				? this.toggleEditWizard(!this.editWizard)
				: this.showEditDialog();
		},

		showEditDialog() {
			this.$refs.renameSceneDialog.pop(
				this.renameScene,
				this.confirmRemoveScene
			);
		},

		toggleEditWizard(mode = true) {
			this.$store.commit("scenes/setEditMode", mode);
		},

		async confirmRemoveScene(payload) {
			let confirmed = await this.$refs.confirmRemoveDialog.pop();
			if (!confirmed) return;

			await this.removeScene(payload);
		},
	},
};
</script>
